// The peer mesh of online play, without WebRTC: routing (direct, or through the room host when a pair of guests
// has no direct link), pings and round trips, peer states from what is heard, and the per-peer limits. Peers are
// room ids here (one byte); the session (session.ts) maps them to session indexes. Links (one per other peer, two
// channels) are made by rtc.ts in the browser and by fakes in the unit tests.
import {
  channelMax, decodeWire, encodeWire, NET, PEER_CONNECTED, PEER_GONE, PEER_SILENT, pingSummary, RateLimit, SEND_NO_PEER,
  SEND_OK, SEND_REFUSED, type Ctl, type Wire,
} from '../../shared/net.ts';

/** One connection to one peer: "r" reliable ordered, "u" unreliable unordered. */
export interface Link {
  open(reliable: boolean): boolean;
  send(reliable: boolean, bytes: Uint8Array<ArrayBuffer>): boolean;
  bufferedR(): number;
  close(): void;
}

export interface MeshEvents {
  /** A game packet (channel 0 or 1) from peer `from`. */
  packet(from: number, ch: number, bytes: Uint8Array): void;
  ctl(from: number, msg: Ctl): void;
  blob(from: number, offset: number, total: number, bytes: Uint8Array): void;
  /** A peer's state changed (PEER_CONNECTED, PEER_SILENT, PEER_GONE). */
  state(id: number, state: number): void;
}

interface Peer {
  id: number;
  link: Link | null;
  heard: number; // last time anything arrived from it (0: never)
  state: number; // -1 until first heard
  samples: number[];
  rtt: number; // smoothed, -1 unknown
  inLimit: [RateLimit, RateLimit];
  outLimit: [RateLimit, RateLimit];
}

const SAMPLES = 20;

export class Mesh {
  readonly me: number;
  hostId: number;
  private peers = new Map<number, Peer>();
  private ev: MeshEvents;
  private now: () => number;
  private lastPing = 0;
  dropped = 0; // incoming messages refused (malformed or over the limits), for tests and the panel

  constructor(me: number, hostId: number, ev: MeshEvents, now: () => number = () => performance.now()) {
    this.me = me; this.hostId = hostId; this.ev = ev; this.now = now;
  }

  private peer(id: number): Peer {
    let p = this.peers.get(id);
    if (!p) {
      const t = this.now();
      p = {
        id, link: null, heard: 0, state: -1, samples: [], rtt: -1,
        inLimit: [new RateLimit(NET.unreliableRate, t), new RateLimit(NET.reliableRate, t)],
        outLimit: [new RateLimit(NET.unreliableRate, t), new RateLimit(NET.reliableRate, t)],
      };
      this.peers.set(id, p);
    }
    return p;
  }

  ids(): number[] { return [...this.peers.keys()]; }
  has(id: number): boolean { return this.peers.has(id); }
  stateOf(id: number): number { return this.peers.get(id)?.state ?? -1; }
  rttOf(id: number): number { return this.peers.get(id)?.rtt ?? -1; }
  summary(id: number) { return pingSummary(this.peers.get(id)?.samples ?? []); }
  direct(id: number): boolean { return !!this.peers.get(id)?.link?.open(true); }

  /** Attach (or replace) the link to a peer. */
  setLink(id: number, link: Link) {
    const p = this.peer(id);
    if (p.link && p.link !== link) p.link.close();
    p.link = link;
  }

  /** The link to a peer closed (or failed). A guest stays reachable through the room host, if there is one. */
  linkClosed(id: number, link: Link) {
    const p = this.peers.get(id);
    if (!p || p.link !== link) return;
    p.link = null;
    if (id === this.hostId || this.me === this.hostId) this.gone(id);
  }

  /** The peer is gone for good (it said bye, left the room, or went silent too long). */
  gone(id: number) {
    const p = this.peers.get(id);
    if (!p || p.state === PEER_GONE) return;
    p.state = PEER_GONE; // first: closing the link calls back into linkClosed
    const link = p.link;
    p.link = null;
    link?.close();
    this.ev.state(id, PEER_GONE);
  }

  /** Forget everything (leaving). */
  closeAll() {
    const all = [...this.peers.values()];
    this.peers.clear(); // first: closing a link calls back into linkClosed
    for (const p of all) p.link?.close();
  }

  // ---- sending -----------------------------------------------------------------------------------------

  /** Send an encoded wire message to a peer: directly, or through the room host. */
  private route(to: number, reliable: boolean, bytes: Uint8Array<ArrayBuffer>): boolean {
    const p = this.peers.get(to);
    if (!p || p.state === PEER_GONE) return false;
    if (p.link?.open(reliable)) return p.link.send(reliable, bytes);
    if (this.me === this.hostId || to === this.hostId) return false;
    const host = this.peers.get(this.hostId);
    if (!host?.link?.open(reliable) || host.state === PEER_GONE) return false;
    return host.link.send(reliable, encodeWire({ k: 'relay-to', to, inner: bytes }));
  }

  /** A game packet (channel 0 or 1) to one peer. Returns SEND_OK, SEND_REFUSED or SEND_NO_PEER. */
  sendGame(to: number, ch: number, payload: Uint8Array): number {
    const p = this.peers.get(to);
    if (!p || p.state === PEER_GONE || to === this.me) return SEND_NO_PEER;
    if (channelMax(ch) < 0 || payload.length > channelMax(ch)) return SEND_REFUSED;
    if (ch === 1 && this.bufferedTo(to) > NET.reliableBuffered) return SEND_REFUSED;
    if (!p.outLimit[ch].allow(payload.length, this.now())) return SEND_REFUSED;
    return this.route(to, ch === 1, encodeWire({ k: 'data', payload })) ? SEND_OK : SEND_NO_PEER;
  }

  private bufferedTo(to: number): number {
    const p = this.peers.get(to);
    if (p?.link?.open(true)) return p.link.bufferedR();
    return this.peers.get(this.hostId)?.link?.bufferedR() ?? 0;
  }

  sendCtl(to: number, msg: Ctl): boolean { return this.route(to, true, encodeWire({ k: 'ctl', msg })); }

  sendBlob(to: number, offset: number, total: number, bytes: Uint8Array): boolean {
    return this.route(to, true, encodeWire({ k: 'blob', offset, total, bytes }));
  }

  // ---- receiving ---------------------------------------------------------------------------------------

  /** A message arrived on the link to peer `id`. */
  receive(id: number, reliable: boolean, bytes: Uint8Array) {
    const p = this.peers.get(id);
    if (!p || p.state === PEER_GONE) return;
    if (!p.inLimit[reliable ? 1 : 0].allow(bytes.length, this.now())) { this.dropped++; return; }
    const w = decodeWire(bytes, reliable);
    if (!w) { this.dropped++; return; }
    if (w.k === 'relay-to') {
      // only the room host relays, and only between two other peers it can reach directly
      const dest = this.peers.get(w.to);
      if (this.me !== this.hostId || w.to === id || w.to === this.me || !dest?.link?.open(reliable)) { this.dropped++; return; }
      this.heard(p);
      dest.link.send(reliable, encodeWire({ k: 'relay-from', from: id, inner: new Uint8Array(w.inner) }));
      return;
    }
    if (w.k === 'relay-from') {
      // only from the room host, on behalf of another peer that is still with us
      const from = this.peers.get(w.from);
      if (id !== this.hostId || w.from === this.me || w.from === id || !from || from.state === PEER_GONE) { this.dropped++; return; }
      if (!from.inLimit[reliable ? 1 : 0].allow(w.inner.length, this.now())) { this.dropped++; return; }
      this.heard(p);
      const inner = decodeWire(w.inner, reliable);
      if (inner) this.handle(from, reliable, inner);
      return;
    }
    this.handle(p, reliable, w);
  }

  private handle(p: Peer, reliable: boolean, w: Wire) {
    this.heard(p);
    switch (w.k) {
      case 'data': this.ev.packet(p.id, reliable ? 1 : 0, w.payload); break;
      case 'ping': this.route(p.id, false, encodeWire({ k: 'pong', t: w.t })); break;
      case 'pong': {
        const rtt = this.now() - w.t;
        if (rtt >= 0 && rtt < 60000) {
          p.samples.push(rtt);
          if (p.samples.length > SAMPLES) p.samples.shift();
          p.rtt = p.rtt < 0 ? rtt : p.rtt * 0.8 + rtt * 0.2;
        }
        break;
      }
      case 'ctl': this.ev.ctl(p.id, w.msg); break;
      case 'blob': this.ev.blob(p.id, w.offset, w.total, w.bytes); break;
      default: break;
    }
  }

  private heard(p: Peer) {
    p.heard = this.now();
    if (p.state !== PEER_CONNECTED && p.state !== PEER_GONE) {
      p.state = PEER_CONNECTED;
      this.ev.state(p.id, PEER_CONNECTED);
    }
  }

  /** Call often (every ~100 ms): pings every peer and updates silent / gone states. */
  tick() {
    const now = this.now();
    if (now - this.lastPing >= NET.pingMs) {
      this.lastPing = now;
      for (const p of this.peers.values()) if (p.state !== PEER_GONE) this.route(p.id, false, encodeWire({ k: 'ping', t: now }));
    }
    for (const p of this.peers.values()) {
      if (p.state === PEER_GONE || p.state === -1) continue;
      const quiet = now - p.heard;
      if (quiet >= NET.goneMs) this.gone(p.id);
      else if (quiet >= NET.silentMs && p.state === PEER_CONNECTED) { p.state = PEER_SILENT; this.ev.state(p.id, PEER_SILENT); }
    }
  }
}
